import { useState, ChangeEvent } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  OutlinedInput,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { toast } from 'react-toastify';
import { createRewardPeriod } from '@/api/stake-db';
import { CreateRewardPeriodRequest, RewardPeriod } from '@/types/stake-db';

interface RewardPeriodFormProps {
  poolId?: string;
  onSuccess?: () => void;
  existingPeriods?: RewardPeriod[];
}

export default function RewardPeriodForm({ poolId: initialPoolId, onSuccess, existingPeriods = [] }: RewardPeriodFormProps) {
  const [formData, setFormData] = useState({
    poolId: initialPoolId || '',
    ticker: '',
    startAt: '',
    endAt: '',
    totalExpectedAmount: '',
    totalExpectedUsdt: '',
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (field: string) => (event: ChangeEvent<HTMLInputElement>) => {
    setFormData((prev) => ({
      ...prev,
      [field]: event.target.value,
    }));
  };

  const formatDateToISO = (dateString: string) => {
    if (!dateString) return '';
    // date 타입 입력값 (YYYY-MM-DD)을 ISO 형식으로 변환
    return `${dateString}T00:00:00.000Z`;
  };

  const handleSubmit = async () => {
    if (!formData.poolId) {
      toast.error('Pool ID is required. Please access from pool list.');
      return;
    }
    if (!formData.startAt || !formData.endAt) {
      toast.error('Start At, End At are required.');
      return;
    }

    // 기존 리워드 기간과 중복 검사
    const newStart = new Date(formData.startAt);
    const newEnd = new Date(formData.endAt);

    for (const period of existingPeriods) {
      const existingStart = new Date(period.startAt);
      const existingEnd = new Date(period.endAt);

      // 겹치는 조건: newStart < existingEnd AND newEnd > existingStart
      if (newStart < existingEnd && newEnd > existingStart) {
        const existingStartStr = existingStart.toISOString().slice(0, 10);
        const existingEndStr = existingEnd.toISOString().slice(0, 10);
        toast.error(`Period overlaps with existing period (${existingStartStr} ~ ${existingEndStr}).`);
        return;
      }
    }

    setLoading(true);
    try {
      const data: CreateRewardPeriodRequest = {
        poolId: formData.poolId,
        startAt: formatDateToISO(formData.startAt),
        endAt: formatDateToISO(formData.endAt),
      };

      const re = await createRewardPeriod(data);

      if (re.status === 200 || re.status === 201) {
        toast.success(re.data.message || 'Reward period created successfully.');
        setFormData({
          poolId: initialPoolId || '',
          ticker: '',
          startAt: '',
          endAt: '',
          totalExpectedAmount: '',
          totalExpectedUsdt: '',
        });
        onSuccess?.();
      } else {
        toast.error(re.data?.message || 'Failed to create reward period.');
      }
    } catch (error) {
      console.error('Create reward period error:', error);
      toast.error('Failed to create reward period.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2}>
        <Grid xs={12}>
          <Typography variant="h6" gutterBottom>
            Create Reward Period
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new reward period for monthly reward settings.
          </Typography>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>Start At *</InputLabel>
            <OutlinedInput
              value={formData.startAt}
              label="Start At *"
              onChange={handleChange('startAt')}
              type="date"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel shrink>End At *</InputLabel>
            <OutlinedInput
              value={formData.endAt}
              label="End At *"
              onChange={handleChange('endAt')}
              type="date"
              notched
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Ticker (Token Price)</InputLabel>
            <OutlinedInput
              value={formData.ticker}
              label="Ticker (Token Price)"
              onChange={handleChange('ticker')}
              type="number"
              inputProps={{ step: '0.01' }}
              disabled
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Total Expected Amount (Token)</InputLabel>
            <OutlinedInput
              value={formData.totalExpectedAmount}
              label="Total Expected Amount (Token)"
              onChange={handleChange('totalExpectedAmount')}
              type="number"
              disabled
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Total Expected USDT</InputLabel>
            <OutlinedInput
              value={formData.totalExpectedUsdt}
              label="Total Expected USDT"
              onChange={handleChange('totalExpectedUsdt')}
              type="number"
              disabled
            />
          </FormControl>
        </Grid>

        <Grid xs={12} sx={{ mt: 2 }}>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={loading}
          >
            {loading ? 'Processing...' : 'Create Reward Period'}
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
}
